import React, { useEffect, useState } from 'react';
import { Button, Progress } from 'reactstrap';
import { useTour, TOUR_STEPS } from '../../contexts/TourContext';

interface TooltipPosition {
  top: number;
  left: number;
}

const TourOverlay: React.FC = () => {
  const { isActive, currentStep, currentTourStep, nextStep, prevStep, endTour, totalSteps } = useTour();
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>({ top: 0, left: 0 });
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!isActive || !currentTourStep) return;

    // Find the target element
    const targetElement = document.getElementById(currentTourStep.target);

    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      setTargetRect(rect);

      // Calculate tooltip position based on step position preference
      let top = rect.top;
      let left = rect.right + 20; // Default: right side

      if (currentTourStep.position === 'left') {
        left = rect.left - 320;
      } else if (currentTourStep.position === 'top') {
        top = rect.top - 150;
        left = rect.left;
      } else if (currentTourStep.position === 'bottom') {
        top = rect.bottom + 20;
        left = rect.left;
      }

      // Ensure tooltip stays within viewport
      if (left < 20) left = 20;
      if (left > window.innerWidth - 340) left = window.innerWidth - 340;
      if (top < 20) top = 20;
      if (top > window.innerHeight - 200) top = window.innerHeight - 200;

      setTooltipPosition({ top, left });

      // Scroll element into view if needed
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isActive, currentStep, currentTourStep]);

  if (!isActive || !currentTourStep) return null;

  const progress = ((currentStep + 1) / totalSteps) * 100;

  return (
    <>
      {/* Overlay styles */}
      <style>{`
        @keyframes pulse-ring {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          100% {
            transform: scale(1.3);
            opacity: 0;
          }
        }

        @keyframes float-arrow {
          0%, 100% {
            transform: translateX(0);
          }
          50% {
            transform: translateX(10px);
          }
        }

        .tour-spotlight {
          position: fixed;
          z-index: 10000;
          pointer-events: none;
          border: 3px solid #7c3aed;
          border-radius: 12px;
          box-shadow:
            0 0 0 9999px rgba(0, 0, 0, 0.75),
            0 0 20px rgba(124, 58, 237, 0.5),
            inset 0 0 20px rgba(124, 58, 237, 0.1);
          transition: all 0.3s ease;
        }

        .tour-spotlight::before {
          content: '';
          position: absolute;
          top: -6px;
          left: -6px;
          right: -6px;
          bottom: -6px;
          border: 3px solid #7c3aed;
          border-radius: 15px;
          animation: pulse-ring 1.5s ease-out infinite;
        }

        .tour-tooltip {
          position: fixed;
          z-index: 10001;
          width: 300px;
          background: white;
          border-radius: 16px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          overflow: hidden;
          animation: fadeInScale 0.3s ease;
        }

        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .tour-tooltip-header {
          background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
          padding: 16px 20px;
          color: white;
        }

        .tour-tooltip-body {
          padding: 20px;
        }

        .tour-tooltip-footer {
          padding: 16px 20px;
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
        }

        .tour-arrow {
          position: fixed;
          z-index: 10001;
          font-size: 32px;
          color: #7c3aed;
          animation: float-arrow 1s ease-in-out infinite;
          filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
        }

        .tour-step-indicator {
          display: flex;
          gap: 6px;
          justify-content: center;
          margin-bottom: 12px;
        }

        .tour-step-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #e5e7eb;
          transition: all 0.3s ease;
        }

        .tour-step-dot.active {
          background: #7c3aed;
          width: 24px;
          border-radius: 4px;
        }

        .tour-step-dot.completed {
          background: #10b981;
        }
      `}</style>

      {/* Dark overlay with spotlight hole */}
      {targetRect && (
        <div
          className="tour-spotlight"
          style={{
            top: targetRect.top - 8,
            left: targetRect.left - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
          }}
        />
      )}

      {/* Arrow pointing to target */}
      {targetRect && (
        <div
          className="tour-arrow"
          style={{
            top: targetRect.top + targetRect.height / 2 - 16,
            left: targetRect.right + 5,
          }}
        >
          <i className="ri-arrow-left-line"></i>
        </div>
      )}

      {/* Tooltip */}
      <div
        className="tour-tooltip"
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left,
        }}
      >
        {/* Header */}
        <div className="tour-tooltip-header">
          <div className="d-flex align-items-center gap-2 mb-2">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle"
              style={{ width: '32px', height: '32px', background: 'rgba(255,255,255,0.2)' }}
            >
              <i className="ri-compass-3-line fs-18"></i>
            </div>
            <span className="text-white-50 small">Step {currentStep + 1} of {totalSteps}</span>
          </div>
          <h5 className="mb-0 text-white fw-bold">{currentTourStep.title}</h5>
        </div>

        {/* Body */}
        <div className="tour-tooltip-body">
          <p className="text-muted mb-3">{currentTourStep.description}</p>

          {/* Step indicators */}
          <div className="tour-step-indicator">
            {TOUR_STEPS.map((_, index) => (
              <div
                key={index}
                className={`tour-step-dot ${
                  index === currentStep ? 'active' : index < currentStep ? 'completed' : ''
                }`}
              />
            ))}
          </div>

          {/* Progress bar */}
          <Progress
            value={progress}
            style={{ height: '4px' }}
            color="primary"
            className="mb-0"
          />
        </div>

        {/* Footer */}
        <div className="tour-tooltip-footer">
          <div className="d-flex justify-content-between align-items-center">
            <Button
              color="link"
              className="text-muted p-0"
              onClick={endTour}
            >
              Skip Tour
            </Button>
            <div className="d-flex gap-2">
              {currentStep > 0 && (
                <Button
                  color="light"
                  size="sm"
                  onClick={prevStep}
                >
                  <i className="ri-arrow-left-s-line me-1"></i>
                  Back
                </Button>
              )}
              <Button
                color="primary"
                size="sm"
                onClick={nextStep}
                style={{ background: '#7c3aed', border: 'none' }}
              >
                {currentStep === totalSteps - 1 ? (
                  <>
                    Finish
                    <i className="ri-check-line ms-1"></i>
                  </>
                ) : (
                  <>
                    Next
                    <i className="ri-arrow-right-s-line ms-1"></i>
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TourOverlay;
